import './Dashboard.css';
import { NavLink, Routes, Route, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  authAPI,
  hasPrimaryEndpoint,
  getEndpointStatus,
  refreshEndpointStatus,
  adminHasPermission,
  positionAPI,
  historyAPI,
  adminSettingsAPI,
  platformSettingsAPI
} from '../../services';
import { getAdminToken, getCurrentAdmin } from '../../services/auth/authManager';
import { clearEndpoints } from '../../services/endpoints/endpointStore';

// Import all components directly to avoid lazy loading issues
import InstrumentManager from './sections/InstrumentManager/InstrumentManager';
import PositionManagement from './sections/PositionManagement/PositionManagement';
import FundsManagement from './sections/FundsManagement/FundsManagement';
import GroupManagement from './sections/GroupManagement/GroupManagement';
import UserManagement from './sections/UserManagement/UserManagement';
import History from './sections/History/History';
import ServiceManager from './sections/ServiceManager/ServiceManager';
import PublicDNSManager from './sections/PublicDNSManager/PublicDNSManager';
import EngineRoleManager from './sections/EngineRoleManager/EngineRoleManager';
import WebVersionManager from './sections/WebVersionManager/WebVersionManager';
import SystemManager from './sections/SystemManager/SystemManager';
import AdminManagement from './sections/AdminManagement/AdminManagement';
import SubRoleManagement from './sections/AdminManagement/SubRoleManagement';
import ApiKeyManager from './sections/ApiKeyManager/ApiKeyManager';
import LiquidityThresholdManager from './sections/LiquidityThresholdManager/LiquidityThresholdManager';
import OrderAudit from './sections/OrderAudit/OrderAudit';
import PlatformSettingsManager from './sections/PlatformSettingsManager/PlatformSettingsManager';

// Loading component
const LoadingFallback = () => (
  <div className="loading-container">
    <div className="loading-wrapper">
      <div className="loading-spinner"></div>
      <p className="loading-text">Loading...</p>
    </div>
  </div>
);

// Error component
const ErrorFallback = ({ error }) => (
  <div className="error-container">
    <div className="error-content">
      <h2>Something went wrong</h2>
      <p>{error.message}</p>
      <button onClick={() => window.location.reload()}>
        Reload Application
      </button>
    </div>
  </div>
);

// Primary endpoint required component
const PrimaryEndpointRequired = () => (
  <div className="primary-endpoint-container">
    <div className="endpoint-required-card">
      <div className="endpoint-icon">
        <i className="fas fa-server"></i>
      </div>
      <h2>Primary Endpoint Required</h2>
      <p className="endpoint-description">This feature requires a primary endpoint to be available and healthy.</p>
      
      <div className="endpoint-steps">
        <h4>How to proceed:</h4>
        <div className="step-list">
          <div className="step-item">
            <div className="step-number">1</div>
            <p>Navigate to <strong>Public DNS</strong></p>
          </div>
          <div className="step-item">
            <div className="step-number">2</div>
            <p>Add or configure your endpoints</p>
          </div>
          <div className="step-item">
            <div className="step-number">3</div>
            <p>Ensure at least one endpoint is healthy and designated as primary</p>
          </div>
        </div>
      </div>
      
      <a href="/dashboard/users-endpoints" className="btn-primary-large">
        <i className="fas fa-users-cog"></i> Manage Endpoints
      </a>
    </div>
  </div>
);

// Permission required component
const PermissionRequired = ({ permission, featureName }) => (
  <div className="permission-required-container">
    <div className="permission-required-card">
      <div className="permission-icon">
        <i className="fas fa-lock"></i>
      </div>
      <h2>Permission Required</h2>
      <p className="permission-description">
        You don't have permission to access <strong>{featureName}</strong>.
      </p>
      <p className="permission-hint">
        This feature requires the <code>{permission}</code> permission.
        Please contact a super admin if you believe this is an error.
      </p>
    </div>
  </div>
);

// Route wrapper with permission check
const RouteWithPermission = ({ component: Component, requiresPrimary, requiresAuth, featureName, primaryEndpointAvailable, ...props }) => {
  const admin = getCurrentAdmin();
  
  // Check permission first (if specified)
  if (requiresAuth && !adminHasPermission(requiresAuth)) {
    return <PermissionRequired permission={requiresAuth} featureName={featureName} />;
  }

  // Check primary endpoint (if required)
  if (requiresPrimary && !primaryEndpointAvailable) {
    return <PrimaryEndpointRequired />;
  }

  // All checks passed, render the component
  return <Component {...props} />;
};

const WelcomeComponent = ({ systemOverview, recentActivity, tradingVolume, platformName }) => {
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'buy': return 'fa-arrow-up';
      case 'sell': return 'fa-arrow-down';
      case 'deposit': return 'fa-plus';
      case 'withdrawal': return 'fa-minus';
      default: return 'fa-exchange-alt';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'buy': return 'green-bg';
      case 'sell': return 'red-bg';
      case 'deposit': return 'green-bg';
      case 'withdrawal': return 'orange-bg';
      case 'unknown':
      case 'trade': return 'blue-bg';
      default: return 'blue-bg';
    }
  };

  const statCards = [
    {
      label: 'Active Users',
      value: formatNumber(systemOverview?.activeUsers || 0),
      icon: 'fa-users',
      color: 'blue',
      trend: 'neutral',
      trendLabel: '—'
    },
    {
      label: 'Total Volume',
      value: formatCurrency(systemOverview?.totalExposure || 0),
      icon: 'fa-chart-line',
      color: 'green',
      trend: 'neutral',
      trendLabel: '—'
    },
    {
      label: 'Active Instruments',
      value: formatNumber(systemOverview?.activeInstruments || 0),
      icon: 'fa-coins',
      color: 'purple',
      trend: 'neutral',
      trendLabel: '—'
    },
    {
      label: "Today's Trades",
      value: formatNumber(systemOverview?.todayTrades || 0),
      icon: 'fa-exchange-alt',
      color: 'amber',
      trend: 'neutral',
      trendLabel: '—'
    }
  ];

  return (
    <div className="dashboard-home">
      {/* ── Hero Header ── */}
      <div className="dashboard-header-section">
        <div className="welcome-header">
          <h1 className="welcome-title">
            <span className="gradient-text">{platformName}</span>
            <span className="subtitle">Admin Dashboard</span>
          </h1>
          <p className="welcome-description">
            Monitor system health, manage trading operations, and configure your exchange from one centralized console.
          </p>
        </div>
      </div>
      
      {/* ── Stats Row ── */}
      <div className="stats-row">
        {statCards.map((stat, idx) => (
          <div className="stat-card" key={idx}>
            <div className={`stat-icon ${stat.color}`}>
              <i className={`fas ${stat.icon}`}></i>
            </div>
            <div className="stat-content">
              <div className="stat-label">{stat.label}</div>
              <div className="stat-value">{stat.value}</div>
              <div className={`stat-trend ${stat.trend}`}>
                {stat.trend === 'up' && <i className="fas fa-arrow-up"></i>}
                {stat.trend === 'down' && <i className="fas fa-arrow-down"></i>}
                {stat.trendLabel}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* ── Two-Column Grid ── */}
      <div className="dashboard-grid">
        {/* Recent Transactions */}
        <div className="dashboard-card activity-card">
          <div className="card-header">
            <h3><i className="fas fa-clock"></i> Recent Transactions</h3>
            <button className="view-all-btn" onClick={() => window.location.href = '/dashboard/history'}>
              View All <i className="fas fa-arrow-right" style={{fontSize:'0.7rem', marginLeft:'4px'}}></i>
            </button>
          </div>
          <div className="activity-list">
            {recentActivity && recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div className="activity-item" key={index}>
                  <div className={`activity-icon-wrapper ${getActivityColor(activity.type)}`}>
                    <i className={`fas ${getActivityIcon(activity.type)}`}></i>
                  </div>
                  <div className="activity-details">
                    <p className="activity-text">{activity.description || `${activity.type?.toUpperCase()} ${activity.instrument}`}</p>
                    <span className="activity-time">{activity.timestamp ? getRelativeTime(activity.timestamp) : '-'}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="activity-item" style={{justifyContent: 'center', padding: '2rem 0'}}>
                <div className="activity-details" style={{textAlign: 'center'}}>
                  <i className="fas fa-inbox" style={{fontSize:'2rem', color:'var(--admin-text-muted)', marginBottom:'0.75rem', display:'block'}}></i>
                  <p className="activity-text" style={{color:'var(--admin-text-light)'}}>No recent transactions</p>
                  <span className="activity-time">Transactions will appear here once trading begins</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="dashboard-card quick-actions-card">
          <div className="card-header">
            <h3><i className="fas fa-bolt"></i> Quick Actions</h3>
          </div>
          <div className="quick-actions-grid">
            <a href="/dashboard/instruments" className="quick-action-item">
              <div className="quick-action-icon purple-gradient">
                <i className="fas fa-plus-circle"></i>
              </div>
              <span>Add Instrument</span>
            </a>
            
            <a href="/dashboard/users" className="quick-action-item">
              <div className="quick-action-icon orange-gradient">
                <i className="fas fa-user-plus"></i>
              </div>
              <span>Manage Users</span>
            </a>
            
            <a href="/dashboard/services" className="quick-action-item">
              <div className="quick-action-icon pink-gradient">
                <i className="fas fa-cogs"></i>
              </div>
              <span>Services</span>
            </a>
            
            <a href="/dashboard/history" className="quick-action-item">
              <div className="quick-action-icon cyan-gradient">
                <i className="fas fa-history"></i>
              </div>
              <span>Trade History</span>
            </a>
          </div>
        </div>
        
        {/* Trading Volume Chart */}
        <div className="dashboard-card chart-card">
          <div className="card-header">
            <h3><i className="fas fa-chart-bar"></i> Trading Volume by Instrument</h3>
            <div className="chart-controls">
              <button className="chart-btn active">Live</button>
            </div>
          </div>
          <div className="chart-placeholder">
            {tradingVolume && tradingVolume.length > 0 ? (
              <div className="chart-mock">
                {tradingVolume.map((vol, index) => (
                  <div className="chart-bar-wrapper" key={index} title={`${vol.instrument}: ${formatCurrency(vol.exposure)}`}>
                    <div 
                      className="chart-bar" 
                      style={{height: `${Math.max(8, vol.percentage)}%`}}
                    ></div>
                    <span className="chart-label">{vol.instrument}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="chart-mock">
                {['BTC', 'ETH', 'SOL', 'ADA', 'DOT', 'AVAX', 'MATIC'].map((label, i) => (
                  <div className="chart-bar-wrapper" key={i}>
                    <div className="chart-bar" style={{height: `${[60, 80, 45, 90, 70, 85, 55][i]}%`, opacity: 0.3}}></div>
                    <span className="chart-label">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function Dashboard({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [endpointStatus, setEndpointStatus] = useState('checking');
  const [primaryEndpointAvailable, setPrimaryEndpointAvailable] = useState(false);
  const [endpointInfo, setEndpointInfo] = useState(null);
  
  // State for category expansion
  const [expandedCategories, setExpandedCategories] = useState({
    core: true,
    app: true
  });

  const [dashboardData, setDashboardData] = useState({
    systemOverview: null,
    recentActivity: [],
    tradingVolume: []
  });

  // Web version state for API key visibility
  const [webVersion, setWebVersion] = useState(null);
  const [webVersionLoading, setWebVersionLoading] = useState(true);
  const [platformName, setPlatformName] = useState('StandAlone Exchange');
  const [platformIconDataUrl, setPlatformIconDataUrl] = useState('');

  // Fetch web version on mount
  useEffect(() => {
    const fetchWebVersion = async () => {
      try {
        const response = await adminSettingsAPI.getWebVersion();
        console.log('[Dashboard] Web version response:', response);
        
        // Handle nested response structure (response.data.data or response.data)
        const settingsData = response.data?.data || response.data;
        console.log('[Dashboard] Settings data:', settingsData);
        
        if (settingsData && typeof settingsData === 'object') {
          setWebVersion(settingsData.webVersion || 'web2');
        } else {
          setWebVersion('web2');
        }
      } catch (err) {
        console.error('Failed to fetch web version:', err);
        // Default to web2 for safety (restrictive)
        setWebVersion('web2');
      } finally {
        setWebVersionLoading(false);
      }
    };

    const fetchPlatformName = async () => {
      try {
        const response = await platformSettingsAPI.get();
        const data = response.data?.data || response.data;
        if (data && data.platformName) {
          setPlatformName(data.platformName);
        }
        if (data && data.platformIconDataUrl) {
          setPlatformIconDataUrl(data.platformIconDataUrl);
        }
      } catch (err) {
        console.log('[Dashboard] Could not fetch platform name, using default');
      }
    };

    fetchWebVersion();
    fetchPlatformName();
  }, []);

  // Check if API Keys menu should be visible based on web version
  const canViewApiKeys = webVersion === 'web3' && !webVersionLoading;

  // Check authentication and API health
  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // Verify authentication - check both old and new token keys
        const token = localStorage.getItem('admin-token') || getAdminToken();
        if (!token) {
          console.log('No token found in Dashboard, redirecting to login');
          setIsAuthenticated(false);
          navigate('/login');
          return;
        }

        // Check endpoint availability and primary status
        setEndpointStatus('checking');
        
        const endpointStatusInfo = getEndpointStatus();
        const hasPrimary = hasPrimaryEndpoint();
        
        setEndpointInfo(endpointStatusInfo);
        setPrimaryEndpointAvailable(hasPrimary);

        if (endpointStatusInfo.total === 0) {
          console.warn('No endpoints configured');
          setEndpointStatus('no-endpoints');
        } else if (!hasPrimary) {
          console.warn('No primary endpoint confirmed, dashboard features limited');
          setEndpointStatus('no-primary');
        } else if (endpointStatusInfo.healthy === 0) {
          console.warn('No healthy endpoints available');
          setEndpointStatus('unhealthy');
        } else {
          console.log('Primary endpoint available, full functionality enabled');
          setEndpointStatus('ready');
        }

        // Mark dashboard as ready
        setIsReady(true);
        
      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        setError(error);
        setEndpointStatus('error');
        setIsReady(true); // Still show dashboard even with errors
      }
    };

    initializeDashboard();
    
    // Listen for endpoint updates
    const handleEndpointUpdate = (event) => {
      console.log('Endpoints updated:', event.detail);
      const statusInfo = getEndpointStatus();
      const hasPrimary = hasPrimaryEndpoint();
      
      setEndpointInfo(statusInfo);
      setPrimaryEndpointAvailable(hasPrimary);
      
      if (hasPrimary) {
        setEndpointStatus('ready');
      } else {
        setEndpointStatus('no-primary');
      }
    };

    window.addEventListener('endpoints-updated', handleEndpointUpdate);
    
    const handlePlatformSettingsUpdate = (event) => {
      if (event.detail) {
        if (event.detail.platformName) {
          setPlatformName(event.detail.platformName);
        }
        if (event.detail.platformIconDataUrl !== undefined) {
          setPlatformIconDataUrl(event.detail.platformIconDataUrl);
        }
      }
    };
    window.addEventListener('platform-settings-updated', handlePlatformSettingsUpdate);
    
    return () => {
      window.removeEventListener('endpoints-updated', handleEndpointUpdate);
      window.removeEventListener('platform-settings-updated', handlePlatformSettingsUpdate);
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once on mount

  const handleRefreshEndpoints = async () => {
    try {
      setEndpointStatus('checking');
      await refreshEndpointStatus();
      // The event listener will handle the UI update
    } catch (error) {
      console.error('Failed to refresh endpoints:', error);
      setEndpointStatus('error');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [positionsData, historyData] = await Promise.all([
        positionAPI.getAggregatedPositions().catch(e => {
          console.error('Positions API error:', e);
          return null;
        }),
        historyAPI.getAll({ limit: 10, sort: 'closedAt', order: 'desc' }).catch(e => {
          console.error('History API error:', e);
          return { data: { data: [] } };
        })
      ]);

      console.log('[Dashboard] Raw positions data:', JSON.stringify(positionsData));
      console.log('[Dashboard] Raw history data:', JSON.stringify(historyData));

      let positions = {};
      
      if (positionsData) {
        if (positionsData.success && positionsData.data?.data) {
          positions = positionsData.data.data;
        } else if (positionsData.data?.data) {
          positions = positionsData.data.data;
        } else if (typeof positionsData.data === 'object' && positionsData.data !== null) {
          positions = positionsData.data;
        } else if (typeof positionsData === 'object' && positionsData !== null) {
          positions = positionsData;
        }
      }
      
      console.log('[Dashboard] Parsed positions:', positions);
      console.log('[Dashboard] Positions keys:', Object.keys(positions));

      const activeInstruments = Object.keys(positions).length;
      const totalExposure = Object.values(positions).reduce((sum, pos) => sum + (pos.totalExposure || 0), 0);
      const totalUsers = Object.values(positions).reduce((sum, pos) => sum + (pos.userCount || 0), 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let historyItems = [];
      if (historyData?.data) {
        if (Array.isArray(historyData.data)) {
          historyItems = historyData.data;
        } else if (Array.isArray(historyData.data.data)) {
          historyItems = historyData.data.data;
        } else if (historyData.data.data?.data && Array.isArray(historyData.data.data.data)) {
          historyItems = historyData.data.data.data;
        } else if (historyData.data.data?.data?.data && Array.isArray(historyData.data.data.data.data)) {
          historyItems = historyData.data.data.data.data;
        }
      }

      console.log('[Dashboard] History items count:', historyItems.length);
      console.log('[Dashboard] History items sample:', historyItems.slice(0, 2));

      const todayTrades = historyItems.filter(trade => {
        if (!trade.closedAt && !trade.openedAt) return false;
        const tradeDate = new Date(trade.closedAt || trade.openedAt);
        return tradeDate >= today;
      }).length || 0;

      const systemOverview = {
        activeUsers: totalUsers,
        totalExposure: totalExposure,
        activeInstruments: activeInstruments,
        todayTrades: todayTrades
      };

      let recentActivity = [];
      if (Array.isArray(historyItems) && historyItems.length > 0) {
        recentActivity = historyItems.slice(0, 5).map(trade => ({
          type: trade.type || trade.side || 'unknown',
          instrument: trade.instrument || trade.symbol || '-',
          size: trade.size || trade.quantity || 0,
          timestamp: trade.closedAt || trade.openedAt || trade.createdAt,
          description: `${trade.type?.toUpperCase() || trade.side?.toUpperCase() || 'TRADE'} ${trade.instrument || trade.symbol || '-'} - ${trade.size || trade.quantity || 0}`
        }));
      }

      console.log('[Dashboard] Recent activity:', recentActivity);

      const tradingVolume = Object.entries(positions)
        .map(([symbol, data]) => ({
          instrument: symbol,
          exposure: data.totalExposure || 0
        }))
        .sort((a, b) => b.exposure - a.exposure)
        .slice(0, 7);
      
      const maxExposure = Math.max(...tradingVolume.map(v => v.exposure), 1);
      tradingVolume.forEach(v => {
        v.percentage = (v.exposure / maxExposure) * 100;
      });

      setDashboardData({
        systemOverview,
        recentActivity,
        tradingVolume
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  useEffect(() => {
    if (primaryEndpointAvailable) {
      fetchDashboardData();
      const interval = setInterval(fetchDashboardData, 60000);
      return () => clearInterval(interval);
    }
  }, [primaryEndpointAvailable]);

  const handleLogout = () => {
    try {
      console.log('Logging out');
      // Clear endpoints cache
      clearEndpoints();
      authAPI.logout();
      setIsAuthenticated(false);
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear endpoints cache even on error
      clearEndpoints();
      // Force logout even if there's an error
      localStorage.removeItem('admin-token');
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (error) {
    return <ErrorFallback error={error} />;
  }

  if (!isReady) {
    return <LoadingFallback />;
  }

  // Check if user has permission to view Sub-Role Management (which controls Core Initialization visibility)
  const canViewCoreInitialization = adminHasPermission('system.super');

  // Menu structure with categories
  const coreItems = [
    { path: '/dashboard/services', icon: 'fas fa-server', label: 'Services', requiresPrimary: true },
    { path: '/dashboard/public-dns', icon: 'fas fa-globe', label: 'Public DNS', requiresPrimary: false },
    { path: '/dashboard/engine-roles', icon: 'fas fa-crown', label: 'Engine Roles', requiresPrimary: true, requiresAuth: 'engines.read' },
    { path: '/dashboard/web-version', icon: 'fas fa-globe-americas', label: 'Web Version', requiresPrimary: false },
    { path: '/dashboard/system', icon: 'fas fa-tools', label: 'System Manager', requiresPrimary: false, requiresAuth: 'settings.read' },
    { path: '/dashboard/platform-settings', icon: 'fas fa-paint-brush', label: 'Platform Settings', requiresPrimary: false, requiresAuth: 'settings.read' },
    { path: '/dashboard/admins', icon: 'fas fa-user-shield', label: 'Admin Management', requiresPrimary: false, requiresAuth: 'admins.read' },
    { path: '/dashboard/sub-roles', icon: 'fas fa-key', label: 'Sub-Role Management', requiresPrimary: false, requiresAuth: 'system.super' },
    ...(canViewApiKeys ? [{ path: '/dashboard/api-keys', icon: 'fas fa-key', label: 'API Keys', requiresPrimary: false, requiresAuth: 'system.super' }] : [])
  ];

  const menuStructure = {
    ...(canViewCoreInitialization ? {
      core: {
        title: 'Core Initialization',
        icon: 'fas fa-cogs',
        description: 'System configuration & setup',
        items: coreItems
      }
    } : {}),
    app: {
      title: 'Application Logic',
      icon: 'fas fa-chart-line',
      description: 'Trading & business operations',
      items: [
        { path: '/dashboard/instruments', icon: 'fas fa-chart-bar', label: 'Instruments', requiresPrimary: true, requiresAuth: 'instruments.read' },
        { path: '/dashboard/positions', icon: 'fas fa-chart-line', label: 'Position Management', requiresPrimary: true, requiresAuth: 'positions.read' },
        { path: '/dashboard/funds', icon: 'fas fa-money-bill-wave', label: 'Funds Management', requiresPrimary: true, requiresAuth: 'funds.read' },
        { path: '/dashboard/users', icon: 'fas fa-user-cog', label: 'User Management', requiresPrimary: true, requiresAuth: 'users.read' },
        { path: '/dashboard/groups', icon: 'fas fa-users', label: 'Group Management', requiresPrimary: true, requiresAuth: 'groups.read' },
        { path: '/dashboard/history', icon: 'fas fa-history', label: 'History', requiresPrimary: true, requiresAuth: 'engines.viewHistory' },
        { path: '/dashboard/order-audit', icon: 'fas fa-clipboard-list', label: 'Order Audit', requiresPrimary: true, requiresAuth: 'engines.viewHistory' },
        { path: '/dashboard/liquidity-threshold', icon: 'fas fa-balance-scale', label: 'Liquidity Threshold', requiresPrimary: true, requiresAuth: 'settings.read' }
      ]
    }
  };

  return (
    <div className={`dashboard-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Top Navigation Bar */}
      <header className="top-navbar">
        <div className="navbar-left">
          <button 
            className="menu-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'bars' : 'times'}`}></i>
          </button>
          
          <div className="navbar-brand">
            {platformIconDataUrl ? (
              <img src={platformIconDataUrl} alt="Logo" className="navbar-logo" />
            ) : (
              <svg width="32" height="32" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="navbar-logo">
                <rect width="80" height="80" rx="12" fill="#2D3436"/>
                <path d="M16 55L28 40L38 48L52 28L64 38" stroke="#00B894" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="52" cy="28" r="4" fill="#00B894"/>
                <path d="M16 65H64" stroke="#636E72" strokeWidth="2" strokeLinecap="round"/>
                <path d="M20 20V14M30 20V10M40 20V14M50 20V10M60 20V14" stroke="#636E72" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
            <span className="navbar-title">{platformName}</span>
          </div>
        </div>
        
        <div className="navbar-right">
          <button className="navbar-icon-btn">
            <i className="fas fa-bell"></i>
            <span className="notification-badge">3</span>
          </button>
          
          <div className="user-menu">
            <button className="user-menu-btn">
              <span className="user-avatar">AD</span>
              <span>Admin</span>
              <i className="fas fa-chevron-down" style={{fontSize: '0.625rem', opacity: 0.5}}></i>
            </button>
          </div>
          
          <button className="logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </header>
      
      {/* Sidebar Navigation */}
      <aside className="sidebar">
        {/* Sidebar brand header (only visible in dark sidebar) */}
        <div className="sidebar-brand">
          {platformIconDataUrl ? (
            <img src={platformIconDataUrl} alt="Logo" className="sidebar-logo" />
          ) : (
            <svg width="28" height="28" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="80" height="80" rx="12" fill="#1E293B"/>
              <path d="M16 55L28 40L38 48L52 28L64 38" stroke="#00B894" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="52" cy="28" r="4" fill="#00B894"/>
              <path d="M16 65H64" stroke="#475569" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
          <span className="sidebar-brand-text">{platformName.split(' ').map(w => w[0]).join('').substring(0, 3).toUpperCase() || 'SAE'}</span>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {/* Dashboard Home */}
            <li className="nav-item">
              <NavLink 
                to="/dashboard" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                end
              >
                <i className="fas fa-th-large"></i>
                <span className="nav-label">Dashboard</span>
              </NavLink>
            </li>
            
            {/* Category Navigation */}
            {Object.entries(menuStructure).map(([categoryKey, category]) => (
              <li key={categoryKey} className="nav-category">
                <button 
                  className={`category-header ${expandedCategories[categoryKey] ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(categoryKey)}
                >
                  <div className="category-info">
                    <i className={category.icon}></i>
                    <div className="category-text">
                      <span className="category-title">{category.title}</span>
                      {!sidebarCollapsed && (
                        <span className="category-description">{category.description}</span>
                      )}
                    </div>
                  </div>
                  <i className={`fas fa-chevron-down category-arrow ${expandedCategories[categoryKey] ? 'rotated' : ''}`}></i>
                </button>
                
                {expandedCategories[categoryKey] && (
                  <ul className="category-items">
                    {category.items.map((item) => {
                      const hasPermission = item.requiresAuth ? adminHasPermission(item.requiresAuth) : true;
                      
                      return (
                      <li key={item.path} className="nav-item sub-item">
                        {/* Check for requiresAuth permission */}
                        {item.requiresAuth && !hasPermission ? (
                          <div className="nav-link disabled" title={`Permission required: ${item.requiresAuth}`}>
                            <i className={item.icon}></i>
                            <span className="nav-label">{item.label}</span>
                            <i className="fas fa-lock lock-icon"></i>
                          </div>
                        ) : item.requiresPrimary && !primaryEndpointAvailable ? (
                          <div className="nav-link disabled" title="Primary endpoint required">
                            <i className={item.icon}></i>
                            <span className="nav-label">{item.label}</span>
                            <i className="fas fa-lock lock-icon"></i>
                          </div>
                        ) : (
                          <NavLink 
                            to={item.path} 
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                          >
                            <i className={item.icon}></i>
                            <span className="nav-label">{item.label}</span>
                          </NavLink>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-footer-content">
            <span className="sidebar-version-badge">
              <i className="fas fa-code-branch"></i> v2.0
            </span>
            {!webVersionLoading && (
              <span className={`sidebar-mode-badge ${webVersion === 'web3' ? 'mode-web3' : 'mode-web2'}`}>
                <i className={`fas fa-${webVersion === 'web3' ? 'cube' : 'globe'}`}></i>
                {webVersion === 'web3' ? 'Web3' : 'Web2'}
              </span>
            )}
          </div>
        </div>
      </aside>
      
      {/* Main Content Area */}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<WelcomeComponent 
            systemOverview={dashboardData.systemOverview} 
            recentActivity={dashboardData.recentActivity}
            tradingVolume={dashboardData.tradingVolume}
            platformName={platformName}
          />} />
          <Route
            path="/instruments"
            element={
              <RouteWithPermission
                component={InstrumentManager}
                requiresPrimary={true}
                requiresAuth="instruments.read"
                featureName="Instrument Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/positions"
            element={
              <RouteWithPermission
                component={PositionManagement}
                requiresPrimary={true}
                requiresAuth="positions.read"
                featureName="Position Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/funds"
            element={
              <RouteWithPermission
                component={FundsManagement}
                requiresPrimary={true}
                requiresAuth="funds.read"
                featureName="Funds Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/groups"
            element={
              <RouteWithPermission
                component={GroupManagement}
                requiresPrimary={true}
                requiresAuth="groups.read"
                featureName="Group Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/users"
            element={
              <RouteWithPermission
                component={UserManagement}
                requiresPrimary={true}
                requiresAuth="users.read"
                featureName="User Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/history"
            element={
              <RouteWithPermission
                component={History}
                requiresPrimary={true}
                requiresAuth="engines.viewHistory"
                featureName="History"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/order-audit"
            element={
              <RouteWithPermission
                component={OrderAudit}
                requiresPrimary={true}
                requiresAuth="engines.viewHistory"
                featureName="Order Audit"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/services"
            element={
              <RouteWithPermission
                component={ServiceManager}
                requiresPrimary={true}
                featureName="Service Manager"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/web-version"
            element={
              <RouteWithPermission
                component={WebVersionManager}
                featureName="Web Version Manager"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/system"
            element={
              <RouteWithPermission
                component={SystemManager}
                requiresAuth="settings.read"
                featureName="System Manager"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/platform-settings"
            element={
              <RouteWithPermission
                component={PlatformSettingsManager}
                requiresAuth="settings.read"
                featureName="Platform Settings"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/liquidity-threshold"
            element={
              <RouteWithPermission
                component={LiquidityThresholdManager}
                requiresPrimary={true}
                requiresAuth="settings.read"
                featureName="Liquidity Threshold"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/admins"
            element={
              <RouteWithPermission
                component={AdminManagement}
                requiresAuth="admins.read"
                featureName="Admin Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/sub-roles"
            element={
              <RouteWithPermission
                component={SubRoleManagement}
                requiresAuth="system.super"
                featureName="Sub-Role Management"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/public-dns"
            element={
              <RouteWithPermission
                component={PublicDNSManager}
                featureName="Public DNS Manager"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          <Route
            path="/engine-roles"
            element={
              <RouteWithPermission
                component={EngineRoleManager}
                requiresPrimary={true}
                requiresAuth="engines.read"
                featureName="Engine Roles"
                primaryEndpointAvailable={primaryEndpointAvailable}
              />
            }
          />
          {!webVersionLoading && webVersion === 'web3' && (
            <Route
              path="/api-keys"
              element={
                <RouteWithPermission
                  component={ApiKeyManager}
                  requiresAuth="system.super"
                  featureName="API Key Management"
                  primaryEndpointAvailable={primaryEndpointAvailable}
                />
              }
            />
          )}
        </Routes>
      </main>
    </div>
  );
}

export default Dashboard; 